import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import ScenarioResolver from 'resolvers/RTMResolvers/ScenarioResolver';
import {
  lifecycles,
  conditionTypes,
  actionTypes,
  resolverTypes,
  broadcastActions,
  progressionTypes,
} from 'constants/scenario';

import Cache from '../Cache';

export default (() => {
  let scenarioResolver;
  let isRegistered = false;

  const _cache = new Cache();
  const _subscriptions = {};

  const defaultLifecycle = lifecycles.INTERACTIVE_EVALUATE;
  const defaultScenarioName = 'Untitled Scenario';
  const defaultCondition = JSON.stringify({ type: conditionTypes.CHAINED_CONDITION, operator: 'OR', conditions: [] });
  const defaultActions = JSON.stringify([{
    action: actionTypes.CHANGE_PROGRESS,
    resolver: {
      type: resolverTypes.LITERAL,
    },
    context: {
      progressionType: progressionTypes.INTERACTIVE_REPEAT,
    },
  }]);
  const defaultCorrectness = 'none';

  function _getDefinedParams(params) {
    return omitBy(params, isUndefined);
  }

  function _createUnsubscribe(id, listener) {
    return () => {
      if (_subscriptions[id]) {
        const listenerIndex = _subscriptions[id].indexOf(listener);
        _subscriptions[id].splice(listenerIndex, 1);
      }
    };
  }

  function _notifyListeners(parentId) {
    if (_subscriptions[parentId]) {
      const updatedScenarioList = _cache.get(parentId);
      const listeners = _subscriptions[parentId];
      listeners.forEach((listener) => {
        listener(updatedScenarioList);
      });
    }
  }

  function _notifyScenarioListeners(parentId, scenarioId) {
    const scenario = getScenario(parentId, scenarioId);
    const listeners = _subscriptions[scenarioId];
    if (listeners) {
      listeners.forEach((listener) => {
        listener(scenario);
      });
    }
  }

  function _removeScenarioListeners(parentId, scenarioId) {
    delete _subscriptions[scenarioId];
  }

  function _getParsedScenario(scenario) {
    const { condition, actions } = scenario;
    return {
      ...scenario,
      condition: condition && JSON.parse(condition),
      actions: actions && JSON.parse(actions),
    };
  }

  function _parseAndAddScenario(parentId, scenario) {
    const parsedScenario = _getParsedScenario(scenario);
    _cache.save(parentId, parsedScenario);
    _notifyListeners(parentId);
  }

  function _parseAndRemoveScenario(parentId, scenarioId) {
    const scenarioIndex = getScenarioIndex(parentId, scenarioId);
    _removeScenarioListeners(parentId, scenarioId);
    _cache.unsave(parentId, scenarioIndex);
    _notifyListeners(parentId);
  }

  function _parseAndUpdateScenarioList(parentId, scenarioList) {
    const parsedScenarioList = scenarioList.map(
      (scenario) => _getParsedScenario(scenario)
    );
    _cache.set(parentId, parsedScenarioList);
    _notifyListeners(parentId);
  }

  function _parseAndUpdateScenario(parentId, scenarioId, scenario) {
    const scenarioIndex = getScenarioIndex(parentId, scenarioId);
    const parsedScenario = _getParsedScenario(scenario);
    _cache.setIn([parentId, scenarioIndex], parsedScenario);
    _notifyScenarioListeners(parentId, scenarioId);
    _notifyListeners(parentId);
  }
  /*
   *
   * Service Functions:
   *
   */

  // Initialization

  const register = (connectionProvider) => {
    isRegistered = true;
    scenarioResolver = new ScenarioResolver(connectionProvider);
  };

  // Create

  const createScenario = async (
    parentId,
    lifecycle = defaultLifecycle,
    name = defaultScenarioName,
    condition = defaultCondition,
    actions = defaultActions,
    description,
    correctness = defaultCorrectness,
  ) => {
    const options = _getDefinedParams(
      { condition, actions, description, correctness }
    );

    const newScenario = await scenarioResolver.authorScenarioCreate(
      parentId, lifecycle, name, options
    );

    _parseAndAddScenario(parentId, newScenario);
    return newScenario.id;
  };

  // Getters

  const getScenarioList = async (parentId, lifecycle = defaultLifecycle) => {
    const scenarioList = await scenarioResolver.authorScenarioList(
      parentId, lifecycle
    );
    _parseAndUpdateScenarioList(parentId, scenarioList);

    return scenarioList;
  };

  const getScenarioIndex = (parentId, scenarioId) =>
    _cache.get(parentId).findIndex((scenario) => (
      scenario.id === scenarioId
    ));

  const getScenario = (parentId, scenarioId) =>
    _cache.get(parentId).find((scenario) => scenario.id === scenarioId);

  // Setters

  const updateScenario = async (parentId, scenarioId, scenario) => {
    const { name, description, correctness, actions, condition } = scenario;

    const stringifiedActions = actions && JSON.stringify(actions);
    const stringifiedCondition = condition && JSON.stringify(condition);

    const options = _getDefinedParams({
      actions: stringifiedActions,
      condition: stringifiedCondition,
      description,
      correctness,
    });

    const updatedScenario = await scenarioResolver.authorScenarioReplace(
      scenarioId, name, options
    );

    _parseAndUpdateScenario(parentId, scenarioId, updatedScenario);
  };

  // Subscriptions

  const notifyUpdate = async (parentId, lifecycle) => {
    const scenarioList = await scenarioResolver.authorScenarioList(
      parentId, lifecycle
    );
    _parseAndUpdateScenarioList(parentId, scenarioList);
  };

  const subscribeToScenarioList = (parentId, listener, lifecycle = lifecycles.INTERACTIVE_EVALUATE) => {
    _subscriptions[parentId] = _subscriptions[parentId] || [];
    _subscriptions[parentId].push(listener);

    const scenarioList = _cache.get(parentId);
    if (scenarioList) {
      listener(scenarioList);
    } else {
      getScenarioList(parentId, lifecycle);
    }

    return _createUnsubscribe(parentId, listener);
  };

  const subscribeToScenario = (parentId, scenarioId, listener) => {
    _subscriptions[scenarioId] = _subscriptions[scenarioId] || [];
    _subscriptions[scenarioId].push(listener);

    return _createUnsubscribe(scenarioId, listener);
  };

  const receiveScenarioChange = (broadcast) => {
    const { parentElementId, action, lifecycle } = broadcast.response;
    if (action === broadcastActions.CREATED || action === broadcastActions.UPDATED) {
      notifyUpdate(parentElementId, lifecycle);
    }
  };

  const deleteScenario = async (parentId, scenarioId, lifecycle = defaultLifecycle) => {
    const scenarioIndex = getScenarioIndex(parentId, scenarioId);
    const scenarioList = _cache.get(parentId);
    scenarioList.splice(scenarioIndex, 1);
    const splicedScenarioIds = scenarioList.map((scenario) => scenario.id);

    await scenarioResolver.authorScenariosReorder(
      parentId, lifecycle, splicedScenarioIds
    );

    _parseAndRemoveScenario(parentId, scenarioId);
  };

  const reOrderScenario = async (parentId, scenarioList, lifecycle = defaultLifecycle) => {
    const scenarioIds = scenarioList.map((scenario) => scenario.id);

    _cache.set(parentId, scenarioList);
    _notifyListeners(parentId);

    await scenarioResolver.authorScenariosReorder(parentId, lifecycle, scenarioIds);
  };

  return {
    isRegistered: () => isRegistered,

    register,
    createScenario,
    getScenarioList,
    getScenario,
    notifyUpdate,
    updateScenario,
    subscribeToScenarioList,
    subscribeToScenario,
    receiveScenarioChange,
    reOrderScenario,
    deleteScenario,
  };
})();
